import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AufnahmePunktDocument, dateString, punktLabel } from '../../data-model';
import { DataService } from '../../data.service';
import { NavbarService } from '../../navbar.service';
import { LatLng, LatLngBounds, latLng, latLngBounds } from 'leaflet';
import { TransformationService, MapStatusService, StorageService, SelectionService } from '@luzula/luzula-lib';
import { DeleteConfirmationService } from '../../shared/delete-confirmation/delete-confirmation.service';
import { FilterService } from 'src/app/filter.service';
import { ImportService } from 'src/app/import.service';
import { AuthService } from 'src/app/auth.service';
import { ProjectService } from 'src/app/project.service';
import { ToastrService } from 'ngx-toastr';

export type ListFilterMode = 'all' | 'map' | 'selection';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-list',
    templateUrl: './bohrpunkt-list.component.html',
    styleUrls: ['./bohrpunkt-list.component.css'],
    
})
export class BohrpunktListComponent implements OnInit, OnDestroy {
  @Input() bps = new Array<any>;
  bpFilter = new Array<boolean>;
  boundsFilter = new Array<boolean>;
  boundsFilterActive = false;
  currentBounds?: LatLngBounds;
  active?: string;
  show = false;
  filterToggle = true;
  dateString = dateString;
  public points = new Array<LatLng>();

	punktLabel = punktLabel;

	// Filter-Modus: all = alle Punkte, map = Kartenausschnitt, selection = Auswahl
	listFilterMode: ListFilterMode = 'map';
	private selectionSubscription?: Subscription;

	// Suchfeld und Filter-Dropdown
	searchText = '';
	filterNames: string[] = [];
	currentFilterName: string | null = null;

  constructor(
		private cd: ChangeDetectorRef,
		private router: Router,
		public aus: AuthService,
		public ps: ProjectService,
		public ds: DataService,
		public fs: FilterService,
		private is: ImportService,
		private ns: NavbarService,
		private ts: TransformationService,
		private storage: StorageService,
		private mapStatusService: MapStatusService,
		private deleteConfirmation: DeleteConfirmationService,
		private msg: ToastrService,
		public selectionService: SelectionService) {
			this.fs.apply.subscribe(() => {
				this.onApplyFilter();
				// Aktuellen Filternamen aus Storage synchronisieren
				this.currentFilterName = this.storage.getSync('currentFilter');
				this.cd.detectChanges();
			});
			this.fs.filtersUpdated.subscribe(() => {
				this.onApplyFilter();
				this.loadFilterNames();
				this.cd.detectChanges();
			});
			this.mapStatusService.globalMapState().subscribe(state => {
				if (state.bounds) {
					const b = state.bounds as any;
					if (b._southWest && b._northEast) {
						this.currentBounds = latLngBounds(
							latLng(b._southWest.lat, b._southWest.lng),
							latLng(b._northEast.lat, b._northEast.lng)
						);
					} else if (typeof state.bounds.contains === 'function') {
						this.currentBounds = state.bounds;
					}
					this.updateBoundsFilter(state.bounds);
				}
			});
			this.selectionSubscription = this.selectionService.selectionChanged.subscribe(() => {
				// Trigger change detection when selection changes
				this.cd.detectChanges();
			});
			this.loadFilterNames();
			this.currentFilterName = this.storage.getSync('currentFilter');
			this.searchText = this.storage.getSync('searchText') || '';
			const savedListFilterMode = this.storage.getSync('listFilterMode') as ListFilterMode;
			if (savedListFilterMode) {
				this.listFilterMode = savedListFilterMode;
				this.boundsFilterActive = (savedListFilterMode === 'map');
			}
  }

  async ngOnInit() {
    // const docs = await this.ds.getBohrpunkte(false);
    // const bps = docs;
    // const points = new Array<LatLng>();
    // bps.forEach(bp => {
    //   const bpDoc = bp.doc as AufnahmePunktDocument;
    //   points.push(this.ts.fromEPSG5650(bpDoc.header.koordinaten.x, bpDoc.header.koordinaten.y));
    //   this.bps.push(bp.doc);
		// 	this.bpFilter.push(!!this.fs.currentFilterFunc && this.fs.currentFilterFunc(bp.doc));
    // });
    // this.points = points;
		this.updatePoints();
	}
	
	updatePoints() {
    const points = new Array<LatLng>();
    this.bps.forEach(bp => {
      const bpDoc = bp as AufnahmePunktDocument;
			if (!bpDoc?.header)
				console.log(bpDoc);
      points.push(this.ts.from(bpDoc.header.koordinaten));
			this.bpFilter.push(!!this.fs.currentFilterFunc && this.fs.currentFilterFunc(bp.doc));
    });
    this.points = points;
	}

	onApplyFilter() {
		const newFilter = new Array<boolean>(this.bps.length);
		this.bps.forEach((bp: any, i: number) => {
			newFilter[i] = this.fs.filteredDocIds.has(bp._id);
		});
		this.bpFilter = newFilter;
	}
	
  select(id: string) {
    this.active = id;
  }
	
	onDownload(data: any) {
		this.is.exportToXlsx(this.bps.filter((val: any, i: number) => !this.fs.currentFilterFunc || this.bpFilter[i]));
	}

	setListFilterMode(mode: ListFilterMode) {
		this.listFilterMode = mode;
		// Alte Kompatibilität
		this.boundsFilterActive = (mode === 'map');
		this.storage.setSync('listFilterMode', mode);
	}

	updateBoundsFilter(bounds: LatLngBounds) {
		const newFilter = new Array<boolean>(this.bps.length);
		this.points.forEach((point: LatLng, i: number) => {
			newFilter[i] = bounds.contains(point);
		});
		this.boundsFilter = newFilter;
	}

	isVisible(i: number): boolean {
		const bp = this.bps[i];

		// Dreistufiger Filter:
		// 1. Alle/Karte (räumlich)
		switch (this.listFilterMode) {
			case 'map':
				if (!(this.boundsFilter[i] ?? true)) return false;
				break;
			case 'selection':
				if (!this.selectionService.isSelected(bp._id)) return false;
				break;
			case 'all':
			default:
				break;
		}

		// 2. Filter (aus Filtermenü)
		if (this.fs.filteredDocIds.size > 0) {
			if (!this.fs.filteredDocIds.has(bp._id)) return false;
		}

		// 3. Schnellsuche (Textfeld)
		if (!this.matchesSearch(bp)) return false;

		return true;
	}

	getVisibleCount(): number {
		return this.bps.filter((_, i) => this.isVisible(i)).length;
	}

	// Suchfeld-Methoden
	onSearchTextChange(text: string) {
		this.searchText = text;
		this.storage.setSync('searchText', text);
	}

	clearSearch() {
		this.searchText = '';
		this.storage.removeSync('searchText');
	}

	private matchesSearch(bp: any): boolean {
		if (!this.searchText || this.searchText.trim() === '') return true;

		const searchTerms = this.searchText.trim().split(/\s+/).filter(t => t.length > 0);
		if (searchTerms.length === 0) return true;

		const label = this.punktLabel(bp, true)?.toLowerCase() || '';
		const nummer = bp.header?.nummer?.toLowerCase() || '';
		const forstamt = bp.header?.forstamt?.toLowerCase() || '';
		const revier = bp.header?.revier?.toLowerCase() || '';
		const kartierer = bp.header?.aufnahme?.kartierer?.toLowerCase() || '';

		return searchTerms.every(term => {
			const termLower = term.toLowerCase();
			return label.includes(termLower) ||
				nummer.includes(termLower) ||
				forstamt.includes(termLower) ||
				revier.includes(termLower) ||
				kartierer.includes(termLower);
		});
	}

	// Filter-Dropdown-Methoden
	loadFilterNames() {
		this.fs.list_keys().then((names: string[]) => {
			this.filterNames = names;
		});
	}

	applyQuickFilter(name: string) {
		this.currentFilterName = name;
		this.storage.setSync('currentFilter', name);

		this.fs.get(name).then((conditions) => {
			if (conditions) {
				const sw = this.currentBounds?.getSouthWest() || latLng(0, 0);
				const ne = this.currentBounds?.getNorthEast() || latLng(0, 0);
				const minP = this.ts.toEPSG5650(sw);
				const maxP = this.ts.toEPSG5650(ne);
				const bnds = [{ l: minP, u: maxP }];
				this.fs.storeViews(name, conditions, false, bnds, () => this.fs.applyFilter(name));
			} else {
				this.currentFilterName = null;
				this.storage.removeSync('currentFilter');
			}
		});
	}

	clearFilter() {
		this.currentFilterName = null;
		this.storage.removeSync('currentFilter');
		this.fs.resetFilter();
	}

	addVisibleToSelection() {
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisible(i)) {
				ids.push(bp._id);
			}
		});
		this.selectionService.add(ids);
	}

	getVisibleSelectedCount(): number {
		let count = 0;
		this.bps.forEach((bp, i) => {
			if (this.isVisible(i) && this.selectionService.isSelected(bp._id)) {
				count++;
			}
		});
		return count;
	}

	removeVisibleFromSelection() {
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisible(i) && this.selectionService.isSelected(bp._id)) {
				ids.push(bp._id);
			}
		});
		this.selectionService.removeMultiple(ids);
	}

	clearSelection() {
		this.selectionService.clear();
	}

	printSelection() {
		const count = this.selectionService.count();
		if (count === 0) return;
		const ids = this.selectionService.getSelectionArray().join(',');
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids } });
	}

	async deleteSelection() {
		const count = this.selectionService.count();
		if (count === 0) return;

		// Sammle die ausgewählten Punkte mit ihren Daten für die erweiterte Bestätigung
		const selectedPoints = this.bps.filter(bp => this.selectionService.isSelected(bp._id));

		const confirmed = await this.deleteConfirmation.confirmDeleteBulkFast(selectedPoints);
		if (!confirmed) return;

		const ids = this.selectionService.getSelectionArray();
		await this.ds.removeBohrpunkteBulk(ids);
		this.selectionService.clear();
		this.msg.success(`${count} Punkte wurden gelöscht`);
	}

	ngOnDestroy() {
		this.selectionSubscription?.unsubscribe();
	}
}
